import {
  useMemo,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from 'react';
import type { CurrentMax, Exercise } from '@jackedlogs/shared';

import type { GroupOption, SupersetContext } from '../ExercisePicker/ExercisePicker';
import { ExerciseSwapPicker } from './ExerciseSwapPicker';
import { SupersetMenuSheet } from './SupersetMenuSheet';
import { PlateCalcModal } from './PlateCalcModal';
import { groupDisplayLabel } from './loggerUnits';
import { useLoggerGymChip } from './LoggerGymChip';
import { useJoinExistingGroup } from './useJoinExistingGroup';
import { GroupAlignDialog } from './GroupAlignDialog';
import { useReplaceExercise } from './useReplaceExercise';
import { EffortScaleModal } from './EffortScaleModal';
import { useExerciseName } from './exerciseName';
import { EntryTipModal } from './EntryTipModal';
import { RestDefaultPicker } from './RestDefaultPicker';
import type { usePlateCalc } from './usePlateCalc';
import type { useRestPicker } from './useRestPicker';
import type { useTimerDefaults } from './useTimerDefaults';
import type { DraftEntry, LoggerDispatch } from './state/loggerDraft';
import type { WeightUnit } from './state/loggerDraftTypes';
import type { useTheme } from './theme';
import type {
  ExerciseTimerSheetProps,
  LoggerExercisePickerProps,
} from './hostDeps';
import type { AnimationSourceFn } from './ExerciseVisualPager';
import type { ConfirmFn } from '../seams';
import type { ComponentType } from 'react';

// Modal stack for the Logger — exercise picker, swap picker, plate calc,
// entry tip, rest-default picker, group picker. Extracted verbatim out of
// LoggerScreen (Track R). Presentational: it owns each modal's visibility
// wiring and the small render-time derivations they need (superset context,
// group menu members), reading only the props passed in.
// The two callbacks with real branching live in sibling hooks:
// useJoinExistingGroup (set-count alignment) and useReplaceExercise
// (cross-kind confirm + laterality seeding).
//
// RW-25 — the alignment question is a shared <GroupAlignDialog> now, mounted
// below as a SIBLING of the picker rather than inside it: the picker closes
// itself the moment a group is picked, so a dialog living in its tree would
// unmount before it could be read. (It was an Alert before, an OS overlay
// outside the tree, which is why the problem never showed on mobile — and it
// never showed on web either, because an Alert does nothing there at all.)
type LoggerModalsProps = {
  pickerOpen: boolean;
  setPickerOpen: Dispatch<SetStateAction<boolean>>;
  addMany: (slugs: string[]) => void;
  createSuperset: (args: {
    addSlugs: string[];
    pairEntryIdxs: number[];
    name: string;
    targetGroupId?: string;
  }) => void;
  usedSlugs: string[];
  swapEntryIdx: number | null;
  setSwapEntryIdx: Dispatch<SetStateAction<number | null>>;
  effortScaleOpen: boolean;
  setEffortScaleOpen: Dispatch<SetStateAction<boolean>>;
  tipEntryIdx: number | null;
  setTipEntryIdx: Dispatch<SetStateAction<number | null>>;
  /** Per-exercise plan instruction for the "?" tip, resolved per-slug by the
   *  parent from usePlanCues (no longer carried on the draft). */
  tipPlanNote: string | undefined;
  groupPickerEntryIdx: number | null;
  setGroupPickerEntryIdx: Dispatch<SetStateAction<number | null>>;
  supersetMenu: { groupId: string; entryIdx: number } | null;
  setSupersetMenu: Dispatch<
    SetStateAction<{ groupId: string; entryIdx: number } | null>
  >;
  addToGroupId: string | null;
  setAddToGroupId: Dispatch<SetStateAction<string | null>>;
  catalog: Exercise[] | null;
  entries: DraftEntry[];
  /** EQ-1-SWAP-PCTMAX — the athlete's stored MAXes, host-resolved. Null while
   *  they load. Same name and shape as useSetCyclePill's own `currentMaxes`,
   *  deliberately: two Logger consumers of one map should not invent two
   *  vocabularies for it. */
  currentMaxes: Map<string, CurrentMax> | null;
  /** EQ-1 slice D2b — the SESSION's place, for the chip both pickers carry.
   *  Distinct from the per-entry `location` the swap picker ranks by. */
  location: string | null;
  dispatch: LoggerDispatch;
  plate: ReturnType<typeof usePlateCalc>;
  restPicker: ReturnType<typeof useRestPicker>;
  timerDefaults: ReturnType<typeof useTimerDefaults>;
  groupCatalog: ReadonlyArray<GroupOption>;
  defaultUnit: WeightUnit;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
  /** Host-injected slots (RW-18) — see hostDeps.ts. */
  ExercisePickerMount: ComponentType<LoggerExercisePickerProps>;
  ExerciseTimerSheetMount: ComponentType<ExerciseTimerSheetProps>;
  EffortScalePicker: ComponentType<Record<string, never>>;
  /** ⛔ OPTIONAL, and the absence is the FEATURE (PE-26). A mount without
   *  durable-write authority — the prescribe editor — passes nothing, and the
   *  "Save as template" action disappears instead of succeeding silently. A
   *  no-op adapter here looked harmless and was not: the sheet closed as if it
   *  had saved. Same rule LoggerGymChip already states for its ★: no door, no
   *  control. */
  saveExerciseGroup?: (name: string, exerciseSlugs: string[]) => Promise<void>;
  profile: import('./hostDeps').LoggerProfile;
  PainDot?: ComponentType<{ exercise: Exercise }>;
  usePainRiskLabel?: (exercise: Exercise) => string | null;
  /** EQ-1-GYM-COLDSTART — the existing profile door (hostDeps.patchProfile),
   *  narrowed to the one field the swap picker writes. ⛔ Not a new queue:
   *  mobile routes it through the profile outbox, web through its full-doc
   *  PUT, exactly as the rest-default picker already does. */
  /** ⛔ OPTIONAL for the same reason as `saveExerciseGroup` above: absent ⇒
   *  useLoggerGymChip takes its documented "no door, no ★" branch. */
  patchProfile?: (patch: {
    locationUnavailable?: import('@jackedlogs/shared').LocationUnavailable;
    equipmentDefault?: import('@jackedlogs/shared').EquipmentDefault;
    // EQ-1-GYM-DEFAULT — the elected default place, the same door narrowed to
    // one more field (the gym chip's ★, LoggerGymChip).
    defaultLocation?: string;
  }) => Promise<void>;
  notify: (title: string, body: string) => void;
  /** The one confirm seam (seams.ts). Used by the cross-kind swap warning,
   *  which used to reach for react-native's Alert from inside packages/ui and
   *  was therefore a silent no-op on web-rnw. */
  confirm: ConfirmFn;
  /** AN-2 — see hostDeps.animationSource. Optional: absent = no animations. */
  animationSource?: AnimationSourceFn;
};

export function LoggerModals({
  pickerOpen,
  setPickerOpen,
  addMany,
  createSuperset,
  usedSlugs,
  swapEntryIdx,
  setSwapEntryIdx,
  effortScaleOpen,
  setEffortScaleOpen,
  tipEntryIdx,
  setTipEntryIdx,
  tipPlanNote,
  groupPickerEntryIdx,
  setGroupPickerEntryIdx,
  supersetMenu,
  setSupersetMenu,
  addToGroupId,
  setAddToGroupId,
  catalog,
  entries,
  currentMaxes,
  location,
  dispatch,
  plate,
  restPicker,
  timerDefaults,
  groupCatalog,
  defaultUnit,
  theme,
  t,
  ExercisePickerMount,
  ExerciseTimerSheetMount,
  EffortScalePicker,
  saveExerciseGroup,
  profile,
  PainDot,
  usePainRiskLabel,
  patchProfile,
  notify,
  confirm,
  animationSource,
}: LoggerModalsProps): ReactElement {
  const resolveExerciseName = useExerciseName();
  const nameOfSlug = (slug: string): string => {
    const ex = catalog?.find((x) => x.slug === slug);
    return ex ? resolveExerciseName(ex.name) : slug;
  };

  // The picker opens either from the general "Add exercise" button (no
  // context, default Esercizi tab) OR from a card's ＋ (groupPickerEntryIdx —
  // default Superset tab, with the current exercise + existing groups + other
  // WO exercises to pair). One picker, two entry points.
  const saveGroup = saveExerciseGroup;
  const supersetIdx = groupPickerEntryIdx;
  const pickerVisible = pickerOpen || supersetIdx !== null || addToGroupId !== null;
  // Memoised: this object is a prop to ExercisePicker whose open-reset effect
  // depends on it. Without memo it gets a fresh reference on every LoggerScreen
  // re-render (the 1s elapsed-timer ticks one), which would wipe the picker's
  // selection/query mid-edit.
  const supersetContext: SupersetContext | null = useMemo(
    () =>
      supersetIdx !== null
        ? {
            currentName: nameOfSlug(entries[supersetIdx]?.exerciseId ?? ''),
            existingGroups: groupCatalog,
            otherEntries: entries
              .map((e, idx) => ({
                idx,
                slug: e.exerciseId,
                name: nameOfSlug(e.exerciseId),
              }))
              .filter((e) => e.idx !== supersetIdx && entries[e.idx]?.group == null),
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supersetIdx, entries, groupCatalog],
  );
  const closePicker = () => {
    setPickerOpen(false);
    setGroupPickerEntryIdx(null);
    setAddToGroupId(null);
  };

  // Group members in array order, for the ☰ menu (save-as-template slugs +
  // move/remove bounds).
  const menuMembers =
    supersetMenu !== null
      ? entries
          .map((e, idx) => ({ idx, slug: e.exerciseId, gid: e.group?.id }))
          .filter((e) => e.gid === supersetMenu.groupId)
      : [];
  const menuPos = menuMembers.findIndex((m) => m.idx === supersetMenu?.entryIdx);
  const menuGroup =
    supersetMenu !== null
      ? (entries.find((e) => e.group?.id === supersetMenu.groupId)?.group ?? null)
      : null;

  const joinExistingGroup = useJoinExistingGroup({
    supersetIdx,
    entries,
    dispatch,
    defaultUnit,
  });
  const replaceExercise = useReplaceExercise({
    entries,
    dispatch,
    t,
    confirm,
    // R6 — same profile field the picker reads; the silent machine apply must
    // not attach one declared absent where this entry is.
    ...(profile.locationUnavailable
      ? { locationUnavailable: profile.locationUnavailable }
      : {}),
    // EQ-1-PIN-DEFAULT — the elected winner is read on the SILENT path too,
    // not just in the tray: electing one is how the athlete makes the app
    // apply it without asking.
    ...(profile.equipmentDefault
      ? { equipmentDefault: profile.equipmentDefault }
      : {}),
    // EQ-1-SWAP-PCTMAX — lets the swap carry a weight over instead of
    // blanking it. Null while the maxes load, which the chain reads as
    // "cannot convert" and falls through to blank, i.e. today's behaviour.
    currentMaxes,
  });

  // EQ-1 slice D2b — the third and fourth mounts of the ONE place primitive
  // (Antenore: *"nel add e nel replace, quello deve imperativamente utilizzare
  // la stessa primitiva"*). The logger's roster header owns the first, in
  // TimelineLoggerScreen.
  //
  // ⛔ Two calls, not one shared result. `useGymChip` keeps its open/closed
  // state in a per-call `useDisclosure`, so handing the same pair of nodes to
  // both pickers would make opening the tray in one open it in the other. The
  // VALUE is shared regardless — both read the same draft place and dispatch
  // the same action — which is the part that has to be single.
  // EQ-1-GYM-DEFAULT — the ★ that elects a default place rides the SAME two
  // picker mounts (Antenore: the add and replace pickers must use the one
  // primitive), through the existing profile door and the host's notify.
  const gymPin = {
    defaultLocation: profile.defaultLocation,
    patchProfile,
    notify,
  };
  const addGymChip = useLoggerGymChip({ value: location, dispatch, ...gymPin });
  const swapGymChip = useLoggerGymChip({ value: location, dispatch, ...gymPin });

  return (
    <>
      <ExercisePickerMount
        visible={pickerVisible}
        onClose={closePicker}
        onAddMany={(slugs) =>
          addToGroupId !== null
            ? createSuperset({
                addSlugs: slugs,
                pairEntryIdxs: [],
                name: '',
                targetGroupId: addToGroupId,
              })
            : addMany(slugs)
        }
        onCreateSuperset={({ addSlugs, pairEntryIdxs, name }) =>
          createSuperset({
            addSlugs,
            // Include the current entry (the one whose ＋ was tapped) in the
            // new group, ahead of any picked already-present partners.
            pairEntryIdxs:
              supersetIdx !== null ? [supersetIdx, ...pairEntryIdxs] : pairEntryIdxs,
            name,
          })
        }
        onJoinGroup={joinExistingGroup.onPick}
        supersetContext={supersetContext}
        presentSlugs={usedSlugs}
        presentTreatment="mark-workout"
        gymChip={addGymChip}
      />
      <SupersetMenuSheet
        visible={supersetMenu !== null}
        groupLabel={groupDisplayLabel(menuGroup, groupCatalog, t)}
        canMoveUp={menuPos > 0}
        canMoveDown={menuPos >= 0 && menuPos < menuMembers.length - 1}
        onClose={() => setSupersetMenu(null)}
        onSaveTemplate={
          saveGroup
            ? () => {
                if (menuGroup && menuMembers.length >= 2) {
                  void saveGroup(
                    groupDisplayLabel(menuGroup, groupCatalog, t),
                    menuMembers.map((m) => m.slug),
                  );
                }
                setSupersetMenu(null);
              }
            : undefined
        }
        onAddExercise={() => {
          const gid = supersetMenu?.groupId ?? null;
          setSupersetMenu(null);
          setAddToGroupId(gid);
        }}
        onMoveUp={() => {
          if (supersetMenu) {
            dispatch({ type: 'move_group_member', entryIdx: supersetMenu.entryIdx, direction: 'up' });
          }
          setSupersetMenu(null);
        }}
        onMoveDown={() => {
          if (supersetMenu) {
            dispatch({ type: 'move_group_member', entryIdx: supersetMenu.entryIdx, direction: 'down' });
          }
          setSupersetMenu(null);
        }}
        onRemoveMember={() => {
          if (supersetMenu) {
            dispatch({ type: 'remove_group_member', entryIdx: supersetMenu.entryIdx });
          }
          setSupersetMenu(null);
        }}
        onSplit={() => {
          if (supersetMenu) {
            dispatch({ type: 'split_group', groupId: supersetMenu.groupId });
          }
          setSupersetMenu(null);
        }}
      />
      <ExerciseSwapPicker
        visible={swapEntryIdx !== null}
        current={
          swapEntryIdx !== null
            ? (catalog?.find(
                (ex) => ex.slug === entries[swapEntryIdx]?.exerciseId,
              ) ?? null)
            : null
        }
        catalog={catalog}
        // The draft's own instance, so a machine created mid-workout is still
        // in the list next time the picker opens — saved history does not
        // have this session yet.
        currentInstance={
          swapEntryIdx !== null
            ? (entries[swapEntryIdx]?.equipmentInstance ?? null)
            : null
        }
        // EQ-1 slice D — the open session, so a machine coined and loaded
        // today already carries its load signature instead of waiting for the
        // workout to be saved.
        liveEntries={entries}
        // EQ-1 slice D — read off the ENTRY, not the session. More precise for
        // the same cost: it ranks by where this exercise actually is, which
        // still holds if the session moved and this entry did not.
        location={
          swapEntryIdx !== null
            ? (entries[swapEntryIdx]?.location ?? null)
            : null
        }
        unit={defaultUnit}
        gymChip={swapGymChip}
        PainDot={PainDot}
        usePainRiskLabel={usePainRiskLabel}
        // EQ-1-GYM-COLDSTART — the declared absences and the door that writes
        // them. Read off the same host profile every other logger surface uses.
        locationUnavailable={profile.locationUnavailable}
        equipmentDefault={profile.equipmentDefault}
        patchProfile={patchProfile}
        notify={notify}
        onClose={() => setSwapEntryIdx(null)}
        onPick={(next, instance) => {
          if (swapEntryIdx === null) return;
          // EQ-1: `instance` is present only when the picker actually asked.
          // Undefined stays undefined so useReplaceExercise resolves it for
          // the incoming exercise rather than treating silence as an answer.
          replaceExercise(
            swapEntryIdx,
            next,
            () => setSwapEntryIdx(null),
            instance,
          );
        }}
      />
      <PlateCalcModal
        visible={plate.state.visible}
        initialKg={plate.state.targetKg}
        exercise={plate.state.exercise}
        isBodyweight={plate.state.isBodyweight}
        initialNegative={plate.state.initialNegative}
        onClose={plate.close}
        onApply={plate.apply}
      />
      <EffortScaleModal
        visible={effortScaleOpen}
        onClose={() => setEffortScaleOpen(false)}
        EffortScalePicker={EffortScalePicker}
      />
      <EntryTipModal
        profile={profile}
        PainDot={PainDot}
        animationSource={animationSource}
        visible={tipEntryIdx !== null}
        exercise={
          tipEntryIdx !== null
            ? (catalog?.find(
                (ex) => ex.slug === entries[tipEntryIdx]?.exerciseId,
              ) ?? null)
            : null
        }
        catalog={catalog}
        // Plan instruction resolved per-slug by the parent (usePlanCues); it's
        // no longer carried on the draft, so a swapped-in exercise shows ITS
        // instruction (or none), never the replaced one's.
        planNote={tipPlanNote}
        onClose={() => setTipEntryIdx(null)}
        onPickAlternative={(alt) => {
          if (tipEntryIdx === null) return;
          // Same path as the swap picker (kind adoption + cross-kind guard).
          replaceExercise(tipEntryIdx, alt, () => setTipEntryIdx(null));
        }}
      />
      <RestDefaultPicker
        visible={restPicker.visible}
        name={(() => {
          const ex = catalog?.find((x) => x.slug === restPicker.slug);
          if (ex) return resolveExerciseName(ex.name);
          return restPicker.slug ?? '';
        })()}
        value={restPicker.value}
        saveAsGlobal={restPicker.saveAsGlobal}
        saving={restPicker.saving}
        canSavePerExercise={Boolean(restPicker.slug)}
        theme={theme}
        onValueChange={restPicker.setValue}
        onToggleSaveAsGlobal={restPicker.toggleSaveAsGlobal}
        onCancel={restPicker.cancel}
        onConfirm={restPicker.confirm}
      />
      {/* Per-exercise timer defaults (rest + countdown/hold), opened by the ⚙
          on the start-timer affordance. Same sheet the Settings card uses. */}
      <ExerciseTimerSheetMount
        visible={timerDefaults.slug !== null}
        exercise={timerDefaults.exercise}
        restGlobal={timerDefaults.restGlobal}
        holdGlobal={timerDefaults.holdGlobal}
        saving={timerDefaults.saving}
        onSave={timerDefaults.save}
        onRemove={timerDefaults.remove}
        onCancel={timerDefaults.cancel}
      />
      {/* The set-count alignment question, raised when an exercise joins a
          superset whose members carry a different number of sets. */}
      {joinExistingGroup.prompt ? (
        <GroupAlignDialog
          shortest={joinExistingGroup.prompt.shortest}
          longest={joinExistingGroup.prompt.longest}
          onPad={joinExistingGroup.pad}
          onTrim={joinExistingGroup.trim}
          onKeep={joinExistingGroup.keep}
        />
      ) : null}
    </>
  );
}
